<main class="bg-black text-white min-h-screen overflow-x-hidden">

  <!-- Hero -->
  <section class="relative min-h-[65vh] flex items-center pt-24 pb-14 overflow-hidden page-hero-gradient">
    <div class="absolute -top-48 right-0 w-[700px] h-[700px] bg-varroc-accent/5 rounded-full blur-3xl"></div>
    <div class="absolute inset-0 opacity-[0.025]" style="background-image: linear-gradient(rgba(0,170,230,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(0,170,230,0.5) 1px, transparent 1px); background-size: 60px 60px;"></div>

    <div class="relative z-10 max-w-7xl mx-auto px-6 w-full">
      <div class="max-w-3xl">
        <a routerLink="/products/connectiq" class="back-nav" data-aos="fade-down">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4 svg-stroke-gradient"><polyline points="15 18 9 12 15 6"></polyline></svg>
          Back
        </a>
        <h1 class="hero-col font-black mb-5" data-aos="fade-up" data-aos-delay="100">
          Babelfish <span class="text-varroc-accent">Device Integration</span> Module
        </h1>
        <p class="text-varroc-text-gray text-lg leading-relaxed mb-8 max-w-2xl" data-aos="fade-up" data-aos-delay="200">
          A lightweight modular framework that handles multiple device types at scale with independent, fault-isolated services and a clean end-to-end packet lifecycle from reception to persistence.
        </p>
        <div class="flex flex-wrap gap-4" data-aos="fade-up" data-aos-delay="300">
          <a routerLink="/contact-us" class="bg-varroc-accent text-white font-bold px-8 py-4 rounded-md hover:bg-varroc-accent-hover hover:shadow-neon transition-all">
            Request Integration
          </a>
        </div>
      </div>
    </div>
  </section>

  <!-- Sticky Nav -->
  <nav class="sticky-product-nav">
    <div class="max-w-7xl mx-auto px-6">
      <div class="flex overflow-x-auto">
        <a *ngFor="let s of sections" (click)="scrollTo(s.id)" [class.active]="activeSection === s.id" class="cursor-pointer flex-shrink-0">
          <i class='bx text-base' [class]="s.icon"></i> {{ s.label }}
        </a>
      </div>
    </div>
  </nav>

  <!-- Content -->
  <div class="max-w-7xl mx-auto px-6 py-20 space-y-28">

    <!-- Overview -->
    <section id="overview" class="section-anchor">
      <div class="grid md:grid-cols-2 gap-12 items-start">
        <div data-aos="fade-right">
          <span class="text-varroc-accent text-sm font-bold uppercase tracking-widest">What Is Babelfish</span>
          <h2 class="text-4xl font-black mt-3 mb-5">Universal Device Integration at Scale</h2>
          <p class="text-varroc-text-gray leading-relaxed mb-6">Babelfish abstracts the complexity of diverse device protocols and packet formats into a unified processing pipeline. Each device type runs as an isolated, independently scalable service — ensuring fault tolerance and clean separation of concerns.</p>
          <p class="text-varroc-text-gray leading-relaxed">Built as a plug-and-play layer, Babelfish integrates seamlessly with Kafka, MongoDB, and any microservices architecture, enabling ConnectIQ deployments to onboard new device types without platform-wide changes.</p>
        </div>
        <div class="grid grid-cols-2 gap-4" data-aos="fade-left">
          <div class="feature-card text-center">
            <i class='bx bx-devices icon-gradient text-3xl mb-3'></i>
            <div class="text-white font-bold text-sm">Multi-Device Support</div>
          </div>
          <div class="feature-card text-center">
            <i class='bx bx-shield-alt-2 icon-gradient text-3xl mb-3'></i>
            <div class="text-white font-bold text-sm">Fault Isolation</div>
          </div>
          <div class="feature-card text-center">
            <i class='bx bx-expand-alt icon-gradient text-3xl mb-3'></i>
            <div class="text-white font-bold text-sm">Horizontal Scaling</div>
          </div>
          <div class="feature-card text-center">
            <i class='bx bx-plug icon-gradient text-3xl mb-3'></i>
            <div class="text-white font-bold text-sm">Plug-and-Play</div>
          </div>
        </div>
      </div>
    </section>

    <div class="accent-divider"></div>

    <!-- Capabilities -->
    <section id="capabilities" class="section-anchor">
      <div class="mb-12" data-aos="fade-up">
        <span class="text-varroc-accent text-sm font-bold uppercase tracking-widest">Core Capabilities</span>
        <h2 class="text-4xl font-black mt-2 mb-4">What Babelfish Does</h2>
        <p class="text-varroc-text-gray max-w-2xl mr-auto">End-to-end packet handling from ingestion to persistence with full lifecycle visibility and isolation at each stage.</p>
      </div>
      <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        <div *ngFor="let cap of capabilities; let i = index" class="feature-card flex items-start gap-4" data-aos="fade-up" [attr.data-aos-delay]="i * 60">
          <div class="w-8 h-8 bg-varroc-accent/15 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
            <i class='bx bx-check icon-gradient text-lg'></i>
          </div>
          <p class="text-varroc-text-gray text-sm leading-relaxed">{{ cap }}</p>
        </div>
      </div>
    </section>

    <div class="accent-divider"></div>

    <!-- Components -->
    <section id="components" class="section-anchor">
      <div class="mb-12" data-aos="fade-up">
        <span class="text-varroc-accent text-sm font-bold uppercase tracking-widest">Architecture</span>
        <h2 class="text-4xl font-black mt-2 mb-4">Core Components</h2>
        <p class="text-varroc-text-gray max-w-2xl mr-auto">Three primary building blocks that together form the complete device processing pipeline.</p>
      </div>
      <div class="grid md:grid-cols-3 gap-6">
        <div *ngFor="let comp of components; let i = index" class="feature-card" data-aos="fade-up" [attr.data-aos-delay]="i * 80">
          <div class="w-12 h-12 bg-varroc-accent/15 rounded-xl flex items-center justify-center mb-4">
            <i class='bx icon-gradient text-2xl' [class]="comp.icon"></i>
          </div>
          <h3 class="text-white font-bold text-lg mb-3">{{ comp.title }}</h3>
          <p class="text-varroc-text-gray text-sm leading-relaxed">{{ comp.desc }}</p>
        </div>
      </div>
    </section>

    <div class="accent-divider"></div>

    <!-- Command & Control -->
    <section id="control" class="section-anchor">
      <div class="mb-12" data-aos="fade-up">
        <span class="text-varroc-accent text-sm font-bold uppercase tracking-widest">Remote Operations</span>
        <h2 class="text-4xl font-black mt-2 mb-4">Device Command &amp; Control</h2>
        <p class="text-varroc-text-gray max-w-2xl mr-auto">Bi-directional cloud-to-device communication for real-time remote control with full audit trails and status tracking.</p>
      </div>
      <div class="grid md:grid-cols-2 gap-8">
        <div class="feature-card" data-aos="fade-right">
          <h3 class="text-white font-bold text-lg mb-4">Control Framework</h3>
          <ul class="space-y-3">
            <li *ngFor="let item of commandControl" class="flex items-start gap-3 text-varroc-text-gray text-sm">
              <i class='bx bx-check-circle icon-gradient mt-0.5 flex-shrink-0'></i>
              <span>{{ item }}</span>
            </li>
          </ul>
        </div>
        <div class="feature-card" data-aos="fade-left">
          <h3 class="text-white font-bold text-lg mb-4">Supported Commands</h3>
          <div class="space-y-3">
            <div *ngFor="let cmd of supportedCommands" class="border border-varroc-border rounded-xl p-4">
              <div class="text-white font-semibold text-sm mb-1">{{ cmd.label }}</div>
              <div class="text-varroc-text-gray text-xs">{{ cmd.desc }}</div>
            </div>
          </div>
        </div>
      </div>
    </section>

  </div>
</main>
